import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import { uploadFile } from '../../../hooks/transfer/blobClient';

const inferFileType = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes('t4')) return 't4';
  if (n.includes('t1')) return 't1-federal';
  if (n.includes('noa') || n.includes('assessment')) return 'noa';
  if (n.includes('paystub')) return 'paystub';
  if (n.includes('license')
    || n.includes('dl')
    || n.includes('driver')
    || n.includes('passport')) return 'identification';

  return '';
};

export interface UseFileUploadParams {
  applicantId: number | undefined;
  applicationId: number;
}

export interface DocItem {
  file: File;
  type: string;
}

export function useFileUpload({
  applicantId,
  applicationId,
}: UseFileUploadParams): {
    docs: Record<string, DocItem>;
    uploading: boolean;
    validationError: string | null;
    clearFiles: ()=> void;
    addFiles: (files: File[])=> void;
    removeFiles: (name: string)=> void;
    updateFileType: (name: string, newType: string)=> void;
    uploadAllFiles: ()=> Promise<boolean>;
  } {
  const toast = useToast();

  const [docs, setDocs] = useState<Record<string, DocItem>>({});
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const clearFiles = (): void => { setDocs({}); setValidationError(null); };
  const addFiles = (files: File[]): void => {
    setValidationError(null);
    setDocs((prev) => {
      const next = { ...prev };
      files.forEach((f) => {
        if (!next[f.name]) next[f.name] = { file: f, type: inferFileType(f.name) };
      });
      return next;
    });
  };
  const removeFile = (name: string): void => {
    setValidationError(null);
    setDocs((prev) => {
      const { [name]: removedDoc, ...rest } = prev;
      return rest;
    });
  };
  const updateFileType = (name: string, newType: string): void => {
    setValidationError(null);
    setDocs((prev) => ({ ...prev, [name]: { ...prev[name], type: newType } }));
  };

  const uploadAllFiles = useCallback(async (): Promise<boolean> => {
    const items = Object.values(docs);

    if (!applicantId) {
      setValidationError('Applicant is required.');
      return false;
    }

    if (items.length === 0) {
      setValidationError('Please choose at least one file.');
      return false;
    }

    const missingType = items.some((d) => !d.type);
    if (missingType) {
      setValidationError('Select a file type for every file.');
      return false;
    }

    setValidationError(null);
    setUploading(true);

    try {
      await Promise.all(
        items.map((d) => uploadFile(d.file, d.type, applicantId, applicationId)),
      );

      toast({
        title: 'Upload successful',
        status: 'success',
        duration: 5000,
        position: 'top',
      });
      return true;
    } catch {
      toast({
        title: 'Upload failed',
        status: 'error',
        duration: 5000,
        position: 'top',
      });
      return false;
    } finally {
      setUploading(false);
    }
  }, [docs, applicantId, applicationId, toast]);

  return {
    docs,
    uploading,
    validationError,
    clearFiles,
    addFiles,
    removeFiles: removeFile,
    updateFileType,
    uploadAllFiles,
  };
}
